import { getApiUrl } from '../config/api';

export interface PointsStats {
  totalAllocated: number;
  totalUsed: number;
  dailyUsage: number;
  activeUsers: number;
  unlimitedUsers: number;
  conversionRate: number;
}

export interface UserPointsData {
  userId: string;
  email: string;
  plan: string;
  role: string;
  monthlyPoints: number;
  pointsUsed: number;
  pointsRemaining: number;
  dailyUsed?: number;
  dailyLimit?: number;
  isUnlimited: boolean;
  lastReset: string;
}

export interface PointTransaction {
  id: string;
  userId: string;
  amount: number;
  type: 'usage' | 'allocation' | 'adjustment';
  feature?: string;
  description: string;
  createdAt: string;
  metadata?: Record<string, any>;
}

export interface BusinessMetrics {
  revenue: {
    monthly: number;
    daily: number;
    growth: number;
  };
  costs: {
    infrastructure: number;
    perPoint: number;
    margin: number;
  };
  usage: {
    topFeatures: Array<{ feature: string; usage: number; revenue: number }>;
    planEfficiency: Array<{ plan: string; profitability: number }>;
  };
}

class PointsAdminService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  async getSystemStats(): Promise<PointsStats> {
    const response = await fetch(getApiUrl('/admin/points/overview'), {
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch system stats');
    }
    
    const data = await response.json();
    
    // Transform backend response to frontend interface
    return {
      totalAllocated: data.overview?.totalPointsAllocated || 0,
      totalUsed: data.overview?.totalPointsUsed || 0,
      dailyUsage: data.overview?.dailyUsage || 0,
      activeUsers: data.overview?.activeUsersToday || 0,
      unlimitedUsers: data.overview?.unlimitedUsers || 0,
      conversionRate: data.overview?.conversionRate || 0,
    };
  }

  async searchUsers(query: string, plan?: string): Promise<UserPointsData[]> {
    // Use the existing users endpoint and filter for the search
    let url = getApiUrl('/admin/users');
    const params = new URLSearchParams();
    
    if (query) params.append('search', query);
    if (plan) params.append('plan', plan);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to search users');
    }
    
    const data = await response.json();
    
    // Transform users data to UserPointsData format
    return data.users?.map((user: any) => ({
      userId: user.id,
      email: user.email,
      plan: user.plan || 'creator',
      role: user.role || 'user',
      monthlyPoints: user.monthlyPoints || 0,
      pointsUsed: user.pointsUsed || 0,
      pointsRemaining: user.pointsRemaining || user.monthlyPoints || 0,
      dailyUsed: 0,  // Not available in search results
      dailyLimit: 0, // Not available in search results
      isUnlimited: user.plan?.includes('unlimited') || false,
      lastReset: user.lastPointsReset || new Date().toISOString(),
    })) || [];
  }

  async getUserPoints(userId: string): Promise<UserPointsData> {
    const response = await fetch(getApiUrl(`/admin/users/${userId}/points`), {
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch user points');
    }
    
    const data = await response.json();
    
    // Check if user is on a points-enabled plan
    const isPointsEnabled = data.isPointsEnabled;
    const user = data.user || {};
    const balance = data.balance || {};
    
    // Transform response to UserPointsData format
    return {
      userId: data.userId || userId,
      email: user.email || '',
      plan: user.plan || 'creator',
      role: user.role || 'user',
      monthlyPoints: isPointsEnabled ? (balance.monthlyPoints || 0) : 0,
      pointsUsed: isPointsEnabled ? (balance.pointsUsed || 0) : 0,
      pointsRemaining: isPointsEnabled ? (balance.pointsRemaining || 0) : 0,
      dailyUsed: isPointsEnabled ? (balance.dailyUsed || 0) : 0,
      dailyLimit: isPointsEnabled ? (balance.dailyLimit || 0) : 0,
      isUnlimited: isPointsEnabled ? (balance.isUnlimited || false) : false,
      lastReset: balance.lastReset || new Date().toISOString(),
    };
  }

  async adjustUserPoints(
    userId: string, 
    amount: number, 
    reason: string
  ): Promise<PointTransaction> {
    const response = await fetch(getApiUrl(`/admin/users/${userId}/points/adjust`), {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ amount, reason }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to adjust user points');
    }
    
    const data = await response.json();
    
    return {
      id: data.transaction?.id || Date.now().toString(),
      userId,
      amount,
      type: 'adjustment',
      description: reason,
      createdAt: new Date().toISOString(),
    };
  }

  async resetUserPoints(userId: string): Promise<PointTransaction> {
    const response = await fetch(getApiUrl(`/admin/users/${userId}/points/reset`), {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to reset user points');
    }
    
    const data = await response.json();
    
    return {
      id: Date.now().toString(),
      userId,
      amount: data.pointsAfterReset || 0,
      type: 'allocation',
      description: 'Points reset to monthly allocation',
      createdAt: new Date().toISOString(),
    };
  }

  async getUserTransactions(userId: string): Promise<PointTransaction[]> {
    // Use the user points endpoint which includes transaction history
    const response = await fetch(getApiUrl(`/admin/users/${userId}/points`), {
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch user transactions');
    }
    
    const data = await response.json();
    
    // Transform transactions to frontend format
    return data.transactions?.map((tx: any) => ({
      id: tx.id,
      userId: tx.userId,
      amount: tx.amount,
      type: tx.type,
      feature: tx.feature,
      description: tx.description,
      createdAt: tx.createdAt,
      metadata: tx.metadata,
    })) || [];
  }

  async getBusinessMetrics(): Promise<BusinessMetrics> {
    const response = await fetch(getApiUrl('/admin/points/analytics'), {
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch business metrics');
    }
    
    const data = await response.json();
    
    // Transform analytics data to business metrics format
    return {
      revenue: {
        monthly: data.pointsSystem?.monthlyRevenue || 0,
        daily: data.pointsSystem?.dailyRevenue || 0,
        growth: data.pointsSystem?.growthRate || 0,
      },
      costs: {
        infrastructure: data.pointsSystem?.totalCosts || 0,
        perPoint: data.pointsSystem?.costPerPoint || 0,
        margin: data.pointsSystem?.profitMargin || 0,
      },
      usage: {
        topFeatures: data.pointsSystem?.topFeatures || [],
        planEfficiency: data.pointsSystem?.planBreakdown || [],
      },
    };
  }

  async getUnlimitedUsersStatus(): Promise<Array<{
    userId: string;
    email: string;
    dailyUsed: number;
    dailyLimit: number;
    throttleLevel: number;
  }>> {
    // This would need to be added to backend, for now return empty array
    return [];
  }

  async triggerMonthlyAllocation(): Promise<{ message: string; affected: number }> {
    const response = await fetch(getApiUrl('/admin/points/allocate-monthly'), {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to trigger monthly allocation');
    }
    
    const data = await response.json();
    
    return {
      message: data.message || 'Monthly allocation triggered',
      affected: data.affected || 0,
    };
  }

  async changePlan(userId: string, newPlan: string): Promise<{ success: boolean; message: string; user?: any }> {
    const response = await fetch(getApiUrl(`/admin/users/${userId}/plan`), {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ plan: newPlan }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to change user plan');
    }
    
    const data = await response.json();
    
    // Add a small delay to ensure database changes are reflected
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return {
      success: data.success || true,
      message: data.message || `Plan changed to ${newPlan}`,
      user: data.user
    };
  }
}

export const pointsAdminService = new PointsAdminService();

